// ============================================================================
// CommitteesActivityCard — « Activité des comités » (rail accueil E1a)
// ============================================================================
// Aperçu plafonné (5) → drill-in « Tous → » vers /comites (route navigable,
// lien BRAND — §4c « Drill-in Voir tout ») : lien MASQUÉ si collection vide,
// pied quantifié si total > aperçu. Badge jalons done/total = StatusBadge.
// Capability-aware : la carte est masquée sur 403 (employee) par AccueilView.

import { StatusBadge, SkeletonCard, QueryError, EmptyState } from '@gs/ui';
import { UsersRound } from 'lucide-react';
import type { TFunction } from 'i18next';
import { useNavigate } from 'react-router-dom';
import type { CommitteesActivity } from '../../hooks/useAccueil';
import { formatRelative } from './format';
import { RailCard, NAV_LINK } from './RailCard';

export interface CommitteesActivityCardProps {
  data: CommitteesActivity | undefined;
  isLoading: boolean;
  isError: boolean;
  migrationPending: boolean;
  onRetry: () => void;
  locale: string;
  t: TFunction;
}

export function CommitteesActivityCard({
  data, isLoading, isError, migrationPending, onRetry, locale, t,
}: CommitteesActivityCardProps) {
  const navigate = useNavigate();
  if (isLoading) return <SkeletonCard lines={4} />;

  const committees = data?.committees ?? [];
  const total = data?.total ?? 0;

  return (
    <RailCard
      title={t('gest:accueil.committees.title')}
      trailing={
        committees.length > 0 ? (
          <button type="button" onClick={() => navigate('/comites')} className={NAV_LINK}>
            {t('gest:accueil.committees.all')}
          </button>
        ) : undefined
      }
    >
      {migrationPending ? (
        <p className="text-field text-slate-400 dark:text-slate-500">{t('gest:accueil.migrationPending')}</p>
      ) : isError ? (
        <QueryError message={t('gest:accueil.committees.error')} onRetry={onRetry} />
      ) : committees.length === 0 ? (
        <EmptyState
          title={t('gest:accueil.committees.emptyTitle')}
          description={t('gest:accueil.committees.empty')}
          icon={<UsersRound size={32} className="text-slate-300 dark:text-slate-600" aria-hidden />}
        />
      ) : (
        <>
          <ul>
            {committees.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between gap-3 border-b border-slate-100 py-2 last:border-b-0 dark:border-slate-700/60"
              >
                <div className="min-w-0">
                  <p className="truncate text-field font-medium text-slate-700 dark:text-slate-200">{c.name}</p>
                  <p className="text-meta text-slate-400 dark:text-slate-500">
                    {formatRelative(c.lastActivityAt, locale)}
                  </p>
                </div>
                {c.milestonesTotal > 0 ? (
                  <StatusBadge variant="success">{`${c.milestonesDone}/${c.milestonesTotal}`}</StatusBadge>
                ) : (
                  <span className="text-meta text-slate-300 dark:text-slate-600">—</span>
                )}
              </li>
            ))}
          </ul>
          {total > committees.length && (
            <div className="mt-2.5 border-t border-slate-100 pt-2.5 dark:border-slate-700/60">
              <button type="button" onClick={() => navigate('/comites')} className={NAV_LINK}>
                {t('gest:accueil.committees.viewAll', { count: total })}
              </button>
            </div>
          )}
        </>
      )}
    </RailCard>
  );
}
